import { map, get, isNil, toNumber, trim, upperFirst, round } from 'lodash';

// services.quantity is decimal(10,2)
export const MAX_QUANTITY = 99999999.99;

// Up to 8 digits and 2 decimals, typed plain ("1000", "0,5", "0.5") or with "." grouping thousands ("1.000", "12.500,5").
// A group starts with 1-9, so "0.500" stops at "0.50" instead of reading as five hundred. The last two alternatives
// are the states a grouped number goes through while it's typed ("1.000.", "1.000.0").
export const QUANTITY_INPUT_PATTERN = /^(\d{0,8}([.,]\d{0,2})?|([1-9]\d{0,2}\.\d{3}|[1-9]\d?\.\d{3}\.\d{3})(,\d{0,2})?|[1-9]\d{0,2}\.\d{0,3}|[1-9]\d?\.\d{3}\.\d{0,3})$/;

const THOUSANDS_PATTERN = /^[1-9]\d{0,2}(\.\d{3})+(,\d*)?$/;

const UNIT_PLURALS = {
  seringa: 'seringas',
  frasco: 'frascos',
  fio: 'fios',
  sessão: 'sessões',
  disparo: 'disparos',
  unidade: 'unidades',
  ampola: 'ampolas',
  jeringa: 'jeringas',
  hilo: 'hilos',
  sesión: 'sesiones',
  unidad: 'unidades',
};

const toCents = (value: any) => Math.round((toNumber(value) || 0) * 100);

function normalizeServices (inputServices) {
  const services = {};

  map(inputServices, (service) => {
    services[service.id] = service;
  });

  return {
    services,
  };
}

// A sale is by quantity when it recorded a unit price; flat sales have none.
const isByQuantity = (service: ?Object) => !isNil(get(service, 'unitPrice'));

const isTemplateByQuantity = (template: ?Object) => trim(get(template, 'unit')) !== '';

const parseQuantity = (text: any) => {
  let value = trim(isNil(text) ? '' : String(text));
  if (THOUSANDS_PATTERN.test(value)) {
    value = value.replace(/\./g, '');
  }
  const quantity = toNumber(value.replace(',', '.'));
  return Number.isFinite(quantity) ? quantity : NaN;
};

const isValidQuantity = (text: any) => {
  const quantity = parseQuantity(text);
  return quantity > 0 && quantity <= MAX_QUANTITY;
};

// 55 → "55", 0.5 → "0,5", 6000 → "6.000"; "—" when there is no quantity to show
const formatQuantity = (quantity: any) => {
  if (isNil(quantity) || !Number.isFinite(toNumber(quantity))) return '—';
  const [integer, decimals] = round(toNumber(quantity), 2).toFixed(2).split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const significantDecimals = decimals.replace(/0+$/, '');
  return significantDecimals ? `${grouped},${significantDecimals}` : grouped;
};

const unitLabel = (unit: ?string, quantity: any = 1) => {
  const name = trim(unit);
  const key = name.toLowerCase();
  if (toNumber(quantity) < 2 || !Object.prototype.hasOwnProperty.call(UNIT_PLURALS, key)) return name;
  return name === key ? UNIT_PLURALS[key] : upperFirst(UNIT_PLURALS[key]);
};

// "55 U", "2 seringas", and "6 × 1.000 disparos" for a unit that starts with a number
const quantityWithUnit = (quantity: any, unit: ?string) => {
  const amount = formatQuantity(quantity);
  const label = unitLabel(unit, quantity);
  if (!label) return amount;
  return /^\d/.test(label) ? `${amount} × ${label}` : `${amount} ${label}`;
};

// In whole cents and rounded half up, like the API's BigDecimal#round(2): floats would turn 0,5 × 2,01 into 1,00.
const lineTotal = (quantity: any, unitPrice: any) => Math.round((toCents(quantity) * toCents(unitPrice)) / 100) / 100;

// quantity × unit price − price, as the API computes Service#discount; negative for a surcharge
const discount = (quantity: any, unitPrice: any, price: any) => (toCents(lineTotal(quantity, unitPrice)) - toCents(price)) / 100;

// what a quantity input shows: 0.5 → "0,5", without thousands separators
const quantityInputValue = (quantity: any) => String(round(toNumber(quantity), 2)).replace('.', ',');

// The sale Editar Serviço edits, its quantity as the input shows it ("0,5" rather than "0.5")
const editableService = (service: ?Object) => (
  service && isByQuantity(service)
    ? { ...service, quantity: quantityInputValue(service.quantity) }
    : service
);

export default {
  normalizeServices,
  isByQuantity,
  isTemplateByQuantity,
  parseQuantity,
  isValidQuantity,
  formatQuantity,
  unitLabel,
  quantityWithUnit,
  lineTotal,
  discount,
  quantityInputValue,
  editableService,
};
